<template>
    <div
        class="OrganisationHeader"
        :class="{ withLogo: hasVisibleLogo }"
    >
		<div class="Info">
			<div class="OrganisationInfo">
				<h2
					v-skeleton="{
						loading,
						sizes: {
							Small: { width: '7.5625rem', height: '0.75rem' },
						}
					}"
					class="OrganisationName"
					:title="organisationName"
				>
					{{ organisationName }}
				</h2>
			</div>
			<div class="SubHeader">
				<span
					v-skeleton="{
						loading,
						sizes: {
							Small: { width: '11.563rem', height: '0.75rem' },
						},
						margins: {
							Small: '0.25rem 0',
						},
					}"
					class="Locations"
				>
					{{ mainLocation }} <span v-if="extraLocationsCount">+{{ extraLocationsCount }}</span>
				</span>

				<Separator v-if="reviewRating"/>

				<RatingCount
					:reviewRating="reviewRating"
					:loading="loading"
					:clickable="true"
					@click.stop="reviewClick"
				/>
			</div>
		</div>

        <OrganisationLogo
			v-if="hasVisibleLogo"
			class="Logo"
			:imageUrl="optimizedOrganisationUrl"
			:organisationName="organisationName"
			:loading="loading"
			:large="true"
		/>
    </div>
</template>


<script setup lang="ts">
import { computed, PropType } from 'vue';
import OrganisationLogo from '@components/SearchResults/Card/OrganisationLogo/OrganisationLogo.vue';
import { IReviewRating } from '@app/interfaces/dtos/IReviewRating';
import Separator from '@components/Separator/Separator.vue';
import { useMobile } from '@composables/useMobile';

const props = defineProps({
	organisationName: {
		type: String,
		default: '',
	},
	organisationLogoUrl: {
		type: String,
		default: '',
	},
	organisationUrl: {
		type: String,
		default: '',
	},
	programmeId: {
		type: String,
		default: '',
	},
	ranking: {
		type: String,
		required: false,
	},
	showLogo: {
		type: Boolean,
		default: true,
	},
	locations: {
		type: Array as () => string[],
		required: false,
	},
	loading: {
		type: Boolean,
		default: false,
	},
	reviewRating: {
		type: Object as PropType<IReviewRating | undefined>,
		default: undefined,
	},
});

const { isMobile } = useMobile();

const hasVisibleLogo = computed(() => props.showLogo || props.loading);
const optimizedOrganisationUrl = computed(() => {
	if (!props.organisationLogoUrl) {
		return '';
	}

	return `${props.organisationLogoUrl}?auto=format&fit=fill&fill=solid&fill-color=00FFFFFF&w=60&h=60&q=70`;
});

const mainLocation = computed(() => props.locations && props.locations.length > 0 ? props.locations[0] : '');
const extraLocationsCount = computed(() => props.locations && props.locations.length > 1 ? props.locations.length - 1 : 0);
const reviewClick = () => {
	setTimeout(() => {
		window.open(`${props.organisationUrl}?ref=search_card#content:student_reviews`, isMobile.value ? '_self' : '_blank');
	}, 0);
}
</script>

<style lang="scss" scoped>
	.OrganisationHeader {
		width: 100%;
		display: flex;
		justify-content: space-between;
		align-items: flex-start;
		position: relative;

		&.withLogo {
			.Info {
				max-width: calc(100% - 4.25rem);
			}
		}

		.Info {
			display: flex;
			flex-direction: column;
			align-items: flex-start;
			min-width: 0;

			.OrganisationInfo {
				display: flex;
				gap: 0.25rem;
				width: 100%;
				@include MarginAreas(0);

				.OrganisationName {
					display: -webkit-box;
					-webkit-box-orient: vertical;
					-webkit-line-clamp: 2;
					line-clamp: 2;
					overflow: hidden;
					text-overflow: ellipsis;
					color: $Grey-900;

					@include TextStyle(HeadingSmall);
					@include MarginAreas(0);
					@include PaddingAreas(0);
				}
			}

			.SubHeader {
				display: flex;
				align-items: center;
				gap: 0.25rem;

				@include Breakpoint(Small, Medium) {
					flex-direction: column;
					align-items: flex-start;
					gap: 0;
				}
			}

			.Locations {
				color: $Grey-900;
				@include TextStyle(UiSmallRegular);
				@include MarginAreas(0.5, 0);

				span {
					color: $Grey-700;
					@include TextStyle(UiSmallRegular);

				}
			}
		}

		.Logo {
			flex-shrink: 0;
			position: absolute;
			right: 0;

			@include MarginAreas(0, 0, 0, 0.5);
		}
	}
</style>